import type React from 'react';
import type { CountryApiRow } from '@/api/reference-admin';
import { countryEntityConfig } from '@/config/reference-admin-entities';
import { formatDate } from '@/lib/utils';
import {
  type ColumnConfig,
  DataTable,
  type DataTableResponse,
  type FilterConfig,
} from './data-table';

import { DeleteReferenceAdminDialog } from './modals/delete-reference-admin-dialog';
import { EditReferenceAdminModal } from './modals/edit-reference-admin-modal';
import { ReferenceAdminStatus, StatusChip } from './status-chip';

// Export the CountryApiRow type as CountryRow for consistency
export type CountryRow = CountryApiRow;

interface Props {
  data?: DataTableResponse<CountryApiRow> | undefined;
  isLoading?: boolean;
  error?: string | null;
  onFilterChange?: (filterKey: string, value: string | string[]) => void;
  onSortChange?: (sortBy: string, order: 'asc' | 'desc') => void;
  onPageChange?: (page: number) => void;
  onRefresh?: () => void;
  headerActions?: React.ReactNode;
}

export const CountriesTable: React.FC<Props> = ({
  data,
  isLoading = false,
  error = null,
  onFilterChange,
  onSortChange,
  onPageChange,
  onRefresh,
  headerActions,
}) => {
  const columns: ColumnConfig<CountryApiRow>[] = [
    {
      key: 'code',
      label: 'Code',
      sortable: true,
      render: (item) => item.code,
    },
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (item) => item.name,
    },
    {
      key: 'code_alpha3',
      label: 'Alpha-3',
      sortable: true,
      render: (item) => item.code_alpha3 || '—',
    },
    {
      key: 'phone_code',
      label: 'Phone',
      sortable: true,
      render: (item) => item.phone_code || '—',
    },
    {
      key: 'currency_code',
      label: 'Currency',
      sortable: true,
      render: (item) => item.currency?.code || '—',
    },
    {
      key: 'is_active',
      label: 'Status',
      sortable: true,
      align: 'center',
      render: (item) => (
        <StatusChip
          type="reference"
          status={item.is_active ? ReferenceAdminStatus.ACTIVE : ReferenceAdminStatus.INACTIVE}
        />
      ),
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      align: 'right',
      render: (item) => (
        <span className="text-muted-foreground">
          {item.created_at ? formatDate(item.created_at) : '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      sortable: false,
      align: 'center',
      render: (row) => (
        <div className="flex items-center gap-1 justify-center">
          <EditReferenceAdminModal
            config={countryEntityConfig}
            entity={row}
            onSuccess={onRefresh}
          />
          <DeleteReferenceAdminDialog
            config={countryEntityConfig}
            entity={row}
            onSuccess={onRefresh}
          />
        </div>
      ),
    },
  ];

  const filters: FilterConfig[] = [
    {
      key: 'status',
      label: 'Status',
      placeholder: 'Select Status',
      multiSelect: false,
      options: [
        { value: ReferenceAdminStatus.ACTIVE, label: 'Active' },
        { value: ReferenceAdminStatus.INACTIVE, label: 'Inactive' },
      ],
    },
  ];

  return (
    <div className="space-y-4">
      <DataTable
        data={data}
        columns={columns}
        caption="Countries"
        rowHeaderColumnKey="name"
        filters={filters}
        headerActions={headerActions}
        isLoading={isLoading}
        error={error}
        {...(onFilterChange && { onFilterChange })}
        {...(onSortChange && { onSortChange })}
        {...(onPageChange && { onPageChange })}
        emptyMessage="No countries found"
        loadingMessage="Loading countries..."
        defaultSortKey="name"
        defaultSortOrder="asc"
        compact={true}
      />
    </div>
  );
};
